/**
 * Geometry for the "Usage by feature" donut.
 *
 * Kept out of the directive so the folding and the arc maths are unit-testable
 * without a DOM — the template only binds the numbers this returns.
 */

/** Radius of the donut's centre line, in the SVG's own units. */
export const DONUT_RADIUS = 54;

/** Arc length of the full ring. Every dash length below is a slice of this. */
export const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/**
 * The gap punched between neighbouring slices, in the same units. A surface-coloured
 * gap rather than a stroke keeps two similar hues from reading as one arc.
 */
export const DONUT_SEGMENT_GAP = 2;

/**
 * Never draw a slice thinner than this. A feature that used credits should be
 * visible as *something*, the same reason the old usage bars had a minimum width.
 */
const MIN_VISIBLE_ARC = 1.5;

/**
 * Named slices before the rest fold into "Other".
 *
 * This is not an arbitrary choice. A donut invites comparison between every pair of
 * slices at once, so its palette has to clear the validator's all-pairs check, and
 * only the first three categorical slots do (worst pair CVD ΔE 9.2, normal-vision
 * 24.0). A fourth hue puts a pair on screen that fails. Folding is the documented
 * answer, so the fourth feature onward becomes "Other".
 */
export const MAX_NAMED_SLICES = 3;

export interface UsageItem {
    productKey?: string;
    productName: string;
    creditsUsed: number;
}

export interface DonutSegment {
    key: string;
    name: string;
    creditsUsed: number;
    /** 1-based categorical slot; 0 is the neutral "Other" grey. */
    colorIndex: number;
    /** Ready for stroke-dasharray. */
    dashArray: string;
    /** Ready for stroke-dashoffset. */
    dashOffset: number;
    /** e.g. "74%", or "<1%" for a slice too small to round to a whole percent. */
    percentLabel: string;
}

export interface DonutView {
    segments: DonutSegment[];
    total: number;
}

export function toDonutSegments(items: UsageItem[] | null | undefined): DonutView {
    const used = (items || []).filter(item => (item.creditsUsed || 0) > 0);
    const total = used.reduce((sum, item) => sum + item.creditsUsed, 0);

    if (!used.length || total <= 0) return { segments: [], total: 0 };

    const ranked = used.slice().sort((a, b) => b.creditsUsed - a.creditsUsed);
    const named = ranked.slice(0, MAX_NAMED_SLICES);
    const rest = ranked.slice(MAX_NAMED_SLICES);

    const slices: { key: string; name: string; creditsUsed: number; colorIndex: number }[] =
        named.map((item, index) => ({
            key: item.productKey || item.productName,
            name: item.productName,
            creditsUsed: item.creditsUsed,
            colorIndex: index + 1,
        }));

    if (rest.length) {
        slices.push({
            key: '__other__',
            name: 'Other',
            creditsUsed: rest.reduce((sum, item) => sum + item.creditsUsed, 0),
            colorIndex: 0,
        });
    }

    let consumed = 0;
    const segments = slices.map(slice => {
        const fraction = slice.creditsUsed / total;
        const arc = fraction * DONUT_CIRCUMFERENCE;
        // The gap is taken out of the slice, never added to it, so the ring still
        // closes exactly at the full circumference.
        const visible = Math.max(arc - DONUT_SEGMENT_GAP, MIN_VISIBLE_ARC);
        const dashOffset = -consumed;
        consumed += arc;

        return {
            key: slice.key,
            name: slice.name,
            creditsUsed: slice.creditsUsed,
            colorIndex: slice.colorIndex,
            dashArray: `${round(visible)} ${round(DONUT_CIRCUMFERENCE - visible)}`,
            dashOffset: round(dashOffset),
            percentLabel: formatPercent(fraction * 100),
        };
    });

    return { segments, total };
}

/**
 * A feature with real usage never reads as "0%" — that would say it used nothing.
 */
function formatPercent(percent: number): string {
    if (percent > 0 && percent < 0.5) return '<1%';
    return `${Math.round(percent)}%`;
}

function round(value: number): number {
    return Math.round(value * 100) / 100;
}
